import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { professionalProfileApi } from '../../services/api';
import ProfessionalProfileCollection from '../../components/profile/ProfessionalProfileCollection';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';
import MBTI_PRESETS from '../../data/mbti_presets.json';
import { getSuggestions } from '../../data/suggestions';

const visaOptions = [
  'US_Citizen', 'Green_Card', 'H1B', 'F1_OPT', 'J1', 'Other'
];
const employmentTypeOptions = ['Full-time', 'Part-time', 'Contract', 'Internship'];
const remoteOptions = ['Remote', 'Hybrid', 'On-site', 'Flexible'];
const companySizeOptions = ['Startup', 'Small', 'Medium', 'Large'];
const careerLevelOptions = ['Entry', 'Mid', 'Senior', 'Executive'];


const MBTI_OPTIONS = ['INTJ','INTP','ENTJ','ENTP','INFJ','INFP','ENFJ','ENFP','ISTJ','ISFJ','ESTJ','ESFJ','ISTP','ISFP','ESTP','ESFP','Other'];

export default function ProfessionalProfile() {
  const [profiles, setProfiles] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [tab, setTab] = useState('Contact & Links');
  const [skillInput, setSkillInput] = useState({});
  const [skillSuggestions, setSkillSuggestions] = useState({});
  const [showSkillSuggestions, setShowSkillSuggestions] = useState({});
  const [educationSuggestions, setEducationSuggestions] = useState({});
  const [showEducationSuggestions, setShowEducationSuggestions] = useState({});
  const [certificateSuggestions, setCertificateSuggestions] = useState({});
  const [showCertificateSuggestions, setShowCertificateSuggestions] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAllDirections, setShowAllDirections] = useState(false);
  const [showDirectionModal, setShowDirectionModal] = useState(false);
  const [selectedNewDirection, setSelectedNewDirection] = useState(null);
  const [followupAnswers, setFollowupAnswers] = useState({});
  const [followupContextSelections, setFollowupContextSelections] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const hasProfile = useMemo(() => (profiles && profiles.length > 0), [profiles]);

  const mergeSkillsByType = (skills) => {
    if (!skills || !Array.isArray(skills)) return [];
    
    const mergedSkills = {};
    
    skills.forEach(skill => {
      if (skill && skill.type != null && String(skill.type).trim() !== '') {
        const normalizedType = String(skill.type).toLowerCase().trim();
        if (mergedSkills[normalizedType]) {
          // Merge details arrays, removing duplicates
          const existingDetails = mergedSkills[normalizedType].details || [];
          const newDetails = skill.details || [];
          const combinedDetails = [...existingDetails, ...newDetails];
          const uniqueDetails = [...new Set(combinedDetails)];
          mergedSkills[normalizedType].details = uniqueDetails;
        } else {
          mergedSkills[normalizedType] = {
            type: skill.type, // Keep original case
            details: [...(skill.details || [])]
          };
        }
      }
    });
    
    return Object.values(mergedSkills);
  };

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const { data } = await professionalProfileApi.listProfiles();
      setProfiles(data || []);
      const activeProfile = (data && data[0]) || null;
      
      // Merge skills with same type (case-insensitive)
      if (activeProfile?.capabilities?.skills) {
        activeProfile.capabilities.skills = mergeSkillsByType(activeProfile.capabilities.skills);
      }
      
      setActive(activeProfile);
      
      // Add a small delay to ensure UI processes the data changes
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load professional profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  useEffect(() => {
    if (!loading && !hasProfile) {
      setShowPromptModal(true);
    }
  }, [loading, hasProfile]);

  const handleField = (path, value) => {
    setActive(prev => {
      if (path.includes('.')) {
        const keys = path.split('.');
        const newState = { ...(prev || {}) };
        let current = newState;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          } else {
            current[keys[i]] = { ...current[keys[i]] };
          }
          current = current[keys[i]];
        }
        
        current[keys[keys.length - 1]] = value;
        return newState;
      } else {
        return { ...(prev || {}), [path]: value };
      }
    });
  };


  const handleSkillInputChange = (skillGroupIndex, value) => {
    setSkillInput(prev => ({ ...prev, [skillGroupIndex]: value }));
    const suggestions = getSuggestions(value, 'skills');
    setSkillSuggestions(prev => ({ ...prev, [skillGroupIndex]: suggestions }));
    setShowSkillSuggestions(prev => ({ ...prev, [skillGroupIndex]: suggestions.length > 0 }));
  };

  const addSkill = (skillGroupIndex, skillValue) => {
    if (skillValue.trim()) {
      const newSkills = [...(active?.capabilities?.skills || [])];
      newSkills[skillGroupIndex].details = [...(newSkills[skillGroupIndex].details || []), skillValue.trim()];
      handleField('capabilities.skills', newSkills);
      setSkillInput(prev => ({ ...prev, [skillGroupIndex]: '' }));
      setShowSkillSuggestions(prev => ({ ...prev, [skillGroupIndex]: false }));
    }
  };

  const selectSkillSuggestion = (skillGroupIndex, suggestion) => {
    addSkill(skillGroupIndex, suggestion);
  };

  const handleEducationSuggestion = (fieldType, inputValue, educationIndex, fieldKey) => {
    let suggestionType = 'schools'; // default
    if (fieldType === 'major') suggestionType = 'courses';
    
    const suggestions = getSuggestions(inputValue, suggestionType);
    const key = `${educationIndex}-${fieldKey}`;
    setEducationSuggestions(prev => ({ ...prev, [key]: suggestions }));
    setShowEducationSuggestions(prev => ({ ...prev, [key]: suggestions.length > 0 && inputValue.length > 0 }));
  };

  const selectEducationSuggestion = (educationIndex, fieldKey, suggestion) => {
    const newEducations = [...(active?.background?.education || [])];
    newEducations[educationIndex][fieldKey] = suggestion;
    handleField('background.education', newEducations);
    const key = `${educationIndex}-${fieldKey}`;
    setShowEducationSuggestions(prev => ({ ...prev, [key]: false }));
  };

  const handleCertificateSuggestion = (inputValue, certificateIndex, fieldKey) => {
    const suggestions = getSuggestions(inputValue, 'certificates');
    const key = `${certificateIndex}-${fieldKey}`;
    setCertificateSuggestions(prev => ({ ...prev, [key]: suggestions }));
    setShowCertificateSuggestions(prev => ({ ...prev, [key]: suggestions.length > 0 && inputValue.length > 0 }));
  };

  const selectCertificateSuggestion = (certificateIndex, fieldKey, suggestion) => {
    const newCertificates = [...(active?.capabilities?.certificates || [])];
    newCertificates[certificateIndex][fieldKey] = suggestion;
    handleField('capabilities.certificates', newCertificates);
    const key = `${certificateIndex}-${fieldKey}`;
    setShowCertificateSuggestions(prev => ({ ...prev, [key]: false }));
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      if (active && active._id) {
        await professionalProfileApi.updateProfile(active._id, active);
      } else {
        await professionalProfileApi.createProfile(active || {});
        toast.success('Profile created');
        await loadProfiles();
        return;
      }
      toast.success('Profile saved');
      await loadProfiles();
    } catch (e) {
      console.error(e);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteProfile = async () => {
    if (!active || !active._id) {
      toast.error('No profile to delete');
      return;
    }

    setDeleting(true);
    try {
      await professionalProfileApi.deleteProfile(active._id);
      toast.success('Profile deleted successfully');
      setShowDeleteConfirm(false);
      await loadProfiles();
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete profile');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 max-w-md w-full mx-4">
          <div className="text-center">
            {/* Animated loading icon */}
            <div className="w-16 h-16 mx-auto mb-6">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            </div>
            
            {/* Loading text */}
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Loading Your Profile
            </h3>
            <p className="text-gray-600 mb-4">
              We're retrieving your professional information...
            </p>
            
            {/* Progress dots */}
            <div className="flex justify-center space-x-1">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <PageHeader
            title={pageDescriptions['/profile'].title}
            description={pageDescriptions['/profile'].description}
            whatYouCanDo={pageDescriptions['/profile'].whatYouCanDo}
            whatYouShouldDo={pageDescriptions['/profile'].whatYouShouldDo}
            actions={
              <div className="flex items-center space-x-3">
                <button 
                  className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-400 text-white rounded-lg hover:from-primary-600 hover:to-primary-500 transition-all duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                  onClick={() => setShowGuide(true)}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
                  </svg>
                  <span>Guide Me</span>
                </button>
                <button 
                  className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg hover:from-blue-600 hover:to-teal-600 transition-all duration-200 shadow-md hover:shadow-lg flex items-center space-x-2 ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}
                  onClick={saveProfile}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z"/>
                      </svg>
                      <span>Save Profile</span>
                    </>
                  )}
                </button>
                {active && active._id && (
                  <button 
                    className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                    onClick={() => setShowDeleteConfirm(true)}
                    title="Delete Profile"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd"/>
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                    </svg>
                    <span>Delete Profile</span>
                  </button>
                )}
              </div>
            }
          />

          {/* Navigation Tabs */}
          <div className="flex space-x-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
            {[
              'Contact & Links', 'Background', 'Capabilities', 'Engagement', 'Preferences', 'Additional'
            ].map((key, index) => {
              const icons = ['👤', '🎓', '⚡', '🎯', '⚙️', '📝'];
              const colors = [
                'bg-blue-500', 'bg-green-500', 'bg-primary-500', 'bg-yellow-500', 'bg-primary-500', 'bg-orange-500'
              ];
              return (
                <button 
                  key={key} 
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    tab === key 
                      ? `${colors[index]} text-white shadow-md` 
                      : 'text-gray-600 hover:text-gray-800 hover:bg-white'
                  }`} 
                  onClick={() => setTab(key)}
                >
                  <span>{icons[index]}</span>
                  <span className="hidden sm:inline">{key}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">

        {tab === 'Contact & Links' && (
          <section className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              {/* Header */}
              <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Personal Information</h3>
                    <p className="text-sm text-stone-500">Basic contact details and professional summary</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className="font-medium text-stone-700">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Full Name *</label>
                      <input 
                        className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" 
                        placeholder="e.g., John Smith"
                        value={active?.contact_info?.name || ''} 
                        onChange={e => handleField('contact_info.name', e.target.value)} 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Email Address *</label>
                      <input 
                        type="email"
                        className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" 
                        placeholder="john.smith@email.com"
                        value={active?.contact_info?.email || ''} 
                        onChange={e => handleField('contact_info.email', e.target.value)} 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Phone Number</label>
                      <input 
                        type="tel"
                        className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" 
                        placeholder="(555) 123-4567"
                        value={active?.contact_info?.phone || ''} 
                        onChange={e => handleField('contact_info.phone', e.target.value)} 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Location</label>
                      <input 
                        className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" 
                        placeholder="New York, NY"
                        value={active?.contact_info?.location || ''} 
                        onChange={e => handleField('contact_info.location', e.target.value)} 
                      />
                    </div>
                  </div>
                </div>
                
                {/* Professional Summary */}
                <div className="space-y-4">
                  <h4 className="font-medium text-stone-700">Professional Summary</h4>
                  <div>
                    <textarea 
                      className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" 
                      placeholder="Brief overview of your professional background, key skills, and career objectives..."
                      rows="4"
                      value={active?.contact_info?.summary || ''} 
                      onChange={e => handleField('contact_info.summary', e.target.value)} 
                    />
                    <p className="text-xs text-stone-500 mt-1">This appears at the top of your profile to give viewers a quick overview</p>
                  </div>
                </div>
                
                {/* Professional Links */}
                <div className="space-y-4">
                  <h4 className="font-medium text-stone-700">Professional Links</h4>
                  <div className="bg-stone-50/50 rounded-lg p-4 border border-stone-200">
                    {(active?.contact_info?.links || []).length > 0 ? (
                      <div className="space-y-2 mb-4">
                        {(active?.contact_info?.links || []).map((link, index) => (
                          <div key={index} className="group flex items-center space-x-3 bg-white rounded-lg p-3 border border-stone-100 hover:border-stone-200 transition-colors">
                            <div className="flex-shrink-0 w-1.5 h-1.5 bg-stone-400 rounded-full"></div>
                            <input 
                              className="flex-1 border-none focus:ring-0 p-0 text-sm bg-transparent" 
                              placeholder="https://linkedin.com/in/yourname"
                              value={link} 
                              onChange={e => {
                                const newLinks = [...(active?.contact_info?.links || [])];
                                newLinks[index] = e.target.value;
                                handleField('contact_info.links', newLinks);
                              }} 
                            />
                            <button 
                              className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500 transition-all p-1 hover:bg-red-50 rounded"
                              onClick={() => {
                                const newLinks = (active?.contact_info?.links || []).filter((_, i) => i !== index);
                                handleField('contact_info.links', newLinks);
                              }}
                              title="Remove link"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-stone-400 text-sm">No links added yet</div>
                        <div className="text-xs text-stone-400 mt-1">Add your LinkedIn, portfolio, or other professional links</div>
                      </div>
                    )}
                    <button 
                      className="w-full text-stone-600 hover:text-stone-800 text-sm font-medium flex items-center justify-center space-x-2 py-2 border border-dashed border-stone-300 rounded-lg hover:border-stone-400 hover:bg-white transition-colors"
                      onClick={() => {
                        handleField('contact_info.links', [...(active?.contact_info?.links || []), '']);
                      }}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                      </svg>
                      <span>Add Professional Link</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === 'Background' && (
          <section className="space-y-6">            
            <div className="space-y-6">
              {(active?.background?.experience || []).map((exp, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd"/>
                            <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{exp.company || 'Company Name'}</h3>
                          <p className="text-sm text-slate-500">{exp.position || 'Position Title'}</p>
                        </div>
                      </div>
                      <button 
                        className="text-slate-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
                        onClick={() => {
                          const newExp = active.background.experience.filter((_, i) => i !== index);
                          handleField('background.experience', newExp);
                        }}
                        title="Remove experience"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Company & Position */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                          <input 
                            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                            placeholder="e.g., Google Inc."
                            value={exp.company || ''} 
                            onChange={e => {
                              const newExp = [...active.background.experience];
                              newExp[index] = { ...newExp[index], company: e.target.value };
                              handleField('background.experience', newExp);
                            }} 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Position</label>
                          <input 
                            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                            placeholder="e.g., Senior Software Engineer"
                            value={exp.position || ''} 
                            onChange={e => {
                              const newExp = [...active.background.experience];
                              newExp[index] = { ...newExp[index], position: e.target.value };
                              handleField('background.experience', newExp);
                            }} 
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
                          <input 
                            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                            placeholder="Jan 2020"
                            value={exp.start || ''} 
                            onChange={e => {
                              const newExp = [...active.background.experience];
                              newExp[index] = { ...newExp[index], start: e.target.value };
                              handleField('background.experience', newExp);
                            }} 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
                          <input 
                            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                            placeholder="Present"
                            value={exp.end || ''} 
                            onChange={e => {
                              const newExp = [...active.background.experience];
                              newExp[index] = { ...newExp[index], end: e.target.value };
                              handleField('background.experience', newExp);
                            }} 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                          <input 
                            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                            placeholder="San Francisco, CA"
                            value={exp.location || ''} 
                            onChange={e => {
                              const newExp = [...active.background.experience];
                              newExp[index] = { ...newExp[index], location: e.target.value };
                              handleField('background.experience', newExp);
                            }} 
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Achievements - Single textarea for all bullet points */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-3">Key Achievements & Responsibilities</label>
                      <div className="bg-slate-50/50 rounded-lg p-4 border border-slate-200">
                        <textarea 
                          className="w-full px-3 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-white resize-none text-sm leading-relaxed" 
                          rows={6}
                          placeholder="• Led development of user authentication system, reducing login issues by 40%
• Collaborated with cross-functional teams to deliver projects 2 weeks ahead of schedule
• Mentored 3 junior developers, improving team productivity and code quality
• Implemented automated testing that reduced bug reports by 60%"
                          value={(exp.details || []).join('\n')}
                          onChange={e => {
                            const newExp = [...active.background.experience];
                            newExp[index].details = e.target.value.split('\n').map(line => line.trim()).filter(Boolean);
                            handleField('background.experience', newExp);
                          }}
                        />
                        <p className="text-xs text-slate-500 mt-2">Enter your achievements and responsibilities, one per line. Use bullet points (•) for better formatting.</p>
                      </div>
                    </div>

                    {/* Projects Section */}
                    {exp.projects && exp.projects.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-3">Key Projects</label>
                        <div className="space-y-4">
                          {exp.projects.map((project, projectIndex) => (
                            <div key={projectIndex} className="bg-slate-50/50 rounded-lg p-4 border border-slate-200">
                              {project.title && (
                                <h4 className="font-medium text-slate-900 mb-2">{project.title}</h4>
                              )}
                              {project.description && (
                                <p className="text-sm text-slate-600 mb-3">{project.description}</p>
                              )}
                              {project.details && project.details.length > 0 && (
                                <div className="space-y-1">
                                  {project.details.map((detail, detailIndex) => (
                                    <div key={detailIndex} className="text-sm text-slate-700 flex items-start">
                                      <span className="text-slate-400 mr-2">•</span>
                                      <span>{detail}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Add new experience or empty state */}
              {(!active?.background?.experience || active.background.experience.length === 0) ? (
                <div className="text-center py-16 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd"/>
                      <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Professional Experience Added Yet</h3>
                  <p className="text-slate-500 mb-6 max-w-sm mx-auto">Add your work experience to showcase your career progression and achievements</p>
                  <button 
                    className="px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
                    onClick={() => {
                      const newExp = { company: '', position: '', start: '', end: '', location: '', details: [], has_projects: false, projects: [] };
                      handleField('background.experience', [newExp]);
                    }}
                  >
                    Add Your First Experience
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => {
                    const newExp = { company: '', position: '', start: '', end: '', location: '', details: [], has_projects: false, projects: [] };
                    handleField('background.experience', [...(active?.background?.experience || []), newExp]);
                  }}
                  className="w-full bg-white border-2 border-dashed border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-900 font-medium py-4 px-6 rounded-xl transition-all flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                  </svg>
                  <span>Add Another Experience</span>
                </button>
              )}
            </div>
            
            {/* Education Section */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-green-600 text-lg">🎓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Education</h3>
              </div>
              
              {(active?.background?.education || []).map((edu, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <span className="text-green-600 text-lg">🎓</span>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">{edu.school || 'University Name'}</h4>
                          <p className="text-sm text-gray-600">{edu.degree || 'Degree'} in {edu.major || 'Major'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">{edu.start || 'Start'} - {edu.end || 'End'}</span>
                        <button 
                          className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          onClick={() => {
                            const newEducation = (active?.background?.education || []).filter((_, i) => i !== index);
                            handleField('background.education', newEducation);
                          }}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">School/University</label>
                        <input 
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                          placeholder="e.g., Stanford University"
                          value={edu.school || ''} 
                          onChange={e => {
                            const newEducation = [...(active?.background?.education || [])];
                            newEducation[index] = { ...newEducation[index], school: e.target.value };
                            handleField('background.education', newEducation);
                          }} 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Degree</label>
                        <input 
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                          placeholder="e.g., Bachelor of Science"
                          value={edu.degree || ''} 
                          onChange={e => {
                            const newEducation = [...(active?.background?.education || [])];
                            newEducation[index] = { ...newEducation[index], degree: e.target.value };
                            handleField('background.education', newEducation);
                          }} 
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Major/Field</label>
                        <input 
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                          placeholder="e.g., Computer Science"
                          value={edu.major || ''} 
                          onChange={e => {
                            const newEducation = [...(active?.background?.education || [])];
                            newEducation[index] = { ...newEducation[index], major: e.target.value };
                            handleField('background.education', newEducation);
                          }} 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
                        <input 
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                          placeholder="2018"
                          value={edu.start || ''} 
                          onChange={e => {
                            const newEducation = [...(active?.background?.education || [])];
                            newEducation[index] = { ...newEducation[index], start: e.target.value };
                            handleField('background.education', newEducation);
                          }} 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
                        <input 
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                          placeholder="2022"
                          value={edu.end || ''} 
                          onChange={e => {
                            const newEducation = [...(active?.background?.education || [])];
                            newEducation[index] = { ...newEducation[index], end: e.target.value };
                            handleField('background.education', newEducation);
                          }} 
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">GPA (Optional)</label>
                      <input 
                        className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                        placeholder="e.g., 3.8"
                        value={edu.gpa || ''} 
                        onChange={e => {
                          const newEducation = [...(active?.background?.education || [])];
                          newEducation[index] = { ...newEducation[index], gpa: e.target.value };
                          handleField('background.education', newEducation);
                        }} 
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <button 
                onClick={() => {
                  const newEducation = { school: '', degree: '', major: '', start: '', end: '', gpa: '' };
                  handleField('background.education', [...(active?.background?.education || []), newEducation]);
                }}
                className="w-full bg-white border-2 border-dashed border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-900 font-medium py-4 px-6 rounded-xl transition-all flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                </svg>
                <span>Add Another Education</span>
              </button>
            </div>
            
            {/* Activities Section */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                  <span className="text-primary-600 text-lg">🎯</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Activities & Leadership</h3>
              </div>
              
              {(active?.background?.activities || []).map((activity, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm">
                  <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <span className="text-primary-600 text-lg">🎯</span>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">{activity.title || 'Activity Title'}</h4>
                          <p className="text-sm text-gray-600">{activity.organization || 'Organization'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">{activity.start || 'Start'} - {activity.end || 'End'}</span>
                        <button 
                          className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          onClick={() => {
                            const newActivities = (active?.background?.activities || []).filter((_, i) => i !== index);
                            handleField('background.activities', newActivities);
                          }}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Activity Title</label>
                        <input 
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                          placeholder="e.g., President of Computer Science Club"
                          value={activity.title || ''} 
                          onChange={e => {
                            const newActivities = [...(active?.background?.activities || [])];
                            newActivities[index] = { ...newActivities[index], title: e.target.value };
                            handleField('background.activities', newActivities);
                          }} 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Organization</label>
                        <input 
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                          placeholder="e.g., University of California"
                          value={activity.organization || ''} 
                          onChange={e => {
                            const newActivities = [...(active?.background?.activities || [])];
                            newActivities[index] = { ...newActivities[index], organization: e.target.value };
                            handleField('background.activities', newActivities);
                          }} 
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
                        <input 
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                          placeholder="2020"
                          value={activity.start || ''} 
                          onChange={e => {
                            const newActivities = [...(active?.background?.activities || [])];
                            newActivities[index] = { ...newActivities[index], start: e.target.value };
                            handleField('background.activities', newActivities);
                          }} 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
                        <input 
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                          placeholder="2022"
                          value={activity.end || ''} 
                          onChange={e => {
                            const newActivities = [...(active?.background?.activities || [])];
                            newActivities[index] = { ...newActivities[index], end: e.target.value };
                            handleField('background.activities', newActivities);
                          }} 
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                      <textarea 
                        className="w-full border border-slate-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-slate-200 focus:border-slate-400 transition-colors bg-slate-50/50" 
                        rows={3}
                        placeholder="Describe your role and achievements..."
                        value={activity.description || ''} 
                        onChange={e => {
                          const newActivities = [...(active?.background?.activities || [])];
                          newActivities[index] = { ...newActivities[index], description: e.target.value };
                          handleField('background.activities', newActivities);
                        }} 
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              <button 
                onClick={() => {
                  const newActivity = { title: '', organization: '', start: '', end: '', description: '' };
                  handleField('background.activities', [...(active?.background?.activities || []), newActivity]);
                }}
                className="w-full bg-white border-2 border-dashed border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-900 font-medium py-4 px-6 rounded-xl transition-all flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                </svg>
                <span>Add Another Activity</span>
              </button>
            </div>
          </section>
        )}

        {tab === 'Education' && (
          <section className="space-y-6">
            <div className="space-y-6">
              {(active?.background?.education || []).map((edu, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-semibold text-stone-900">{edu.school || 'School/University'}</h3>
                          <p className="text-sm text-stone-500">{edu.major || 'Field of Study'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const newEducations = [...(active?.background?.education || [])];
                          newEducations.splice(index, 1);
                          handleField('background.education', newEducations);
                        }}
                        className="text-stone-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
                        title="Remove education entry"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Academic Information */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative">
                          <label className="block text-sm font-medium text-stone-700 mb-2">School/University</label>
                          <input 
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., Harvard University, Stanford University..."
                            value={edu.school || ''} 
                            onChange={e => {
                              const newEducations = [...(active?.background?.education || [])];
                              newEducations[index].school = e.target.value;
                              handleField('background.education', newEducations);
                              handleEducationSuggestion('school', e.target.value, index, 'school');
                            }}
                            onFocus={() => {
                              handleEducationSuggestion('school', edu.school || '', index, 'school');
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                const key = `${index}-school`;
                                setShowEducationSuggestions(prev => ({ ...prev, [key]: false }));
                              }, 150);
                            }}
                          />
                          {/* School suggestions dropdown */}
                          {showEducationSuggestions[`${index}-school`] && educationSuggestions[`${index}-school`] && educationSuggestions[`${index}-school`].length > 0 && (
                            <div className="absolute z-10 w-full bg-white border border-stone-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                              {educationSuggestions[`${index}-school`].map((suggestion, suggIndex) => (
                                <button
                                  key={suggIndex}
                                  className="w-full text-left px-3 py-2 hover:bg-stone-50 transition-colors border-b border-stone-100 last:border-b-0 text-sm"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectEducationSuggestion(index, 'school', suggestion);
                                  }}
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="relative">
                          <label className="block text-sm font-medium text-stone-700 mb-2">Major/Field of Study</label>
                          <input 
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., Computer Science, Business Administration..."
                            value={edu.major || ''} 
                            onChange={e => {
                              const newEducations = [...(active?.background?.education || [])];
                              newEducations[index].major = e.target.value;
                              handleField('background.education', newEducations);
                              handleEducationSuggestion('major', e.target.value, index, 'major');
                            }}
                            onFocus={() => {
                              handleEducationSuggestion('major', edu.major || '', index, 'major');
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                const key = `${index}-major`;
                                setShowEducationSuggestions(prev => ({ ...prev, [key]: false }));
                              }, 150);
                            }}
                          />
                          {/* Major suggestions dropdown */}
                          {showEducationSuggestions[`${index}-major`] && educationSuggestions[`${index}-major`] && educationSuggestions[`${index}-major`].length > 0 && (
                            <div className="absolute z-10 w-full bg-white border border-stone-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                              {educationSuggestions[`${index}-major`].map((suggestion, suggIndex) => (
                                <button
                                  key={suggIndex}
                                  className="w-full text-left px-3 py-2 hover:bg-stone-50 transition-colors border-b border-stone-100 last:border-b-0 text-sm"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectEducationSuggestion(index, 'major', suggestion);
                                  }}
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Start Date</label>
                          <input 
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="Sep 2018"
                            value={edu.start || ''} 
                            onChange={e => {
                              const newEducations = [...(active?.background?.education || [])];
                              newEducations[index].start = e.target.value;
                              handleField('background.education', newEducations);
                            }} 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">End Date</label>
                          <input 
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="May 2022"
                            value={edu.end || ''} 
                            onChange={e => {
                              const newEducations = [...(active?.background?.education || [])];
                              newEducations[index].end = e.target.value;
                              handleField('background.education', newEducations);
                            }} 
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">GPA (Optional)</label>
                          <input 
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="3.8/4.0"
                            value={edu.gpa || ''} 
                            onChange={e => {
                              const newEducations = [...(active?.background?.education || [])];
                              newEducations[index].gpa = e.target.value;
                              handleField('background.education', newEducations);
                            }} 
                          />
                        </div>
                      </div>
                    </div>

                    {/* Relevant Courses */}
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-3">Relevant Courses</label>
                      <div className="bg-stone-50/50 rounded-lg p-4 border border-stone-200">
                        {(edu.courses || []).length > 0 ? (
                          <div className="space-y-2 mb-4">
                            {(edu.courses || []).map((course, cidx) => (
                              <div key={cidx} className="group flex items-center space-x-3 bg-white rounded-lg p-3 border border-stone-100 hover:border-stone-200 transition-colors">
                                <div className="flex-shrink-0 w-1.5 h-1.5 bg-stone-400 rounded-full"></div>
                                <input 
                                  className="flex-1 border-none focus:ring-0 p-0 text-sm bg-transparent"
                                  placeholder="e.g., Data Structures and Algorithms"
                                  value={course}
                                  onChange={e => {
                                    const newEducations = [...(active?.background?.education || [])];
                                    newEducations[index].courses[cidx] = e.target.value;
                                    handleField('background.education', newEducations);
                                  }}
                                />
                                <button 
                                  onClick={() => {
                                    const newEducations = [...(active?.background?.education || [])];
                                    newEducations[index].courses.splice(cidx, 1);
                                    handleField('background.education', newEducations);
                                  }}
                                  className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500 transition-all p-1 hover:bg-red-50 rounded"
                                  title="Remove course"
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <div className="text-stone-400 text-sm">No courses added yet</div>
                            <div className="text-xs text-stone-400 mt-1">Add relevant courses from your program</div>
                          </div>
                        )}
                        <button 
                          onClick={() => {
                            const newEducations = [...(active?.background?.education || [])];
                            newEducations[index].courses = [...(newEducations[index].courses || []), ''];
                            handleField('background.education', newEducations);
                          }}
                          className="w-full text-stone-600 hover:text-stone-800 text-sm font-medium flex items-center justify-center space-x-2 py-2 border border-dashed border-stone-300 rounded-lg hover:border-stone-400 hover:bg-white transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                          </svg>
                          <span>Add Course</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Empty state or Add New Button */}
              {(!active?.background?.education || active.background.education.length === 0) ? (
                <div className="text-center py-16 bg-stone-50 rounded-xl border-2 border-dashed border-stone-200">
                  <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-stone-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-stone-900 mb-2">No Education Added Yet</h3>
                  <p className="text-stone-500 mb-6 max-w-sm mx-auto">Add your educational background to showcase your academic achievements</p>
                  <button 
                    className="px-6 py-3 bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition-colors font-medium"
                    onClick={() => handleField('background.education', [{ school: '', major: '', start: '', end: '', gpa: '', courses: [] }])}
                  >
                    Add Your First Education
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => handleField('background.education', [...(active?.background?.education || []), { school: '', major: '', start: '', end: '', gpa: '', courses: [] }])}
                  className="w-full bg-white border-2 border-dashed border-stone-200 hover:border-stone-300 text-stone-600 hover:text-stone-900 font-medium py-4 px-6 rounded-xl transition-all flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                  </svg>
                  <span>Add Another Education</span>
                </button>
              )}
            </div>
          </section>
        )}

        {tab === 'Capabilities' && (
          <section className="mt-6">
            <div className="space-y-6">
              {(active?.capabilities?.skills || []).map((skillGroup, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <input
                            className="bg-transparent text-stone-900 text-lg font-semibold placeholder-stone-400 border-b border-stone-200 focus:border-stone-400 w-full focus:outline-none transition-colors"
                            placeholder="e.g., Programming Languages, Tools, etc."
                            value={skillGroup.type || ''}
                            onChange={e => {
                              const newSkills = [...(active?.capabilities?.skills || [])];
                              newSkills[index].type = e.target.value;
                              handleField('capabilities.skills', newSkills);
                            }}
                          />
                          <p className="text-stone-500 text-sm mt-1">Skill category</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const newSkills = [...(active?.capabilities?.skills || [])];
                          newSkills.splice(index, 1);
                          handleField('capabilities.skills', newSkills);
                        }}
                        className="text-stone-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
                        title="Remove skill group"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="bg-stone-50/50 rounded-lg p-4 border border-stone-100">
                      <h4 className="font-medium text-stone-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        Individual Skills
                      </h4>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(skillGroup.details || []).map((skill, skillIndex) => (
                          <div
                            key={skillIndex}
                            className="group flex items-center bg-white rounded-full px-3 py-1.5 border border-stone-200 shadow-sm hover:shadow-md hover:border-stone-300 transition-all duration-200"
                          >
                            <span className="text-sm font-medium text-stone-700">{skill}</span>
                            <button
                              onClick={() => {
                                const newSkills = [...(active?.capabilities?.skills || [])];
                                newSkills[index].details = newSkills[index].details.filter((_, i) => i !== skillIndex);
                                handleField('capabilities.skills', newSkills);
                              }}
                              className="ml-2 text-stone-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                              title="Remove skill"
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                        
                        {(!skillGroup.details || skillGroup.details.length === 0) && (
                          <div className="text-stone-500 text-sm italic">No skills added yet. Start adding your expertise!</div>
                        )}
                      </div>

                      {/* Add new skill input with autocomplete */}
                      <div className="relative">
                        <div className="flex items-center space-x-2">
                          <input
                            className="flex-1 px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white"
                            placeholder="Add a skill (e.g., JavaScript, Python, React...)"
                            value={skillInput[index] || ''}
                            onChange={e => handleSkillInputChange(index, e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addSkill(index, skillInput[index] || '');
                              } else if (e.key === 'Escape') {
                                setShowSkillSuggestions(prev => ({ ...prev, [index]: false }));
                              }
                            }}
                            onFocus={() => {
                              const suggestions = getSuggestions(skillInput[index] || '', 'skills');
                              setSkillSuggestions(prev => ({ ...prev, [index]: suggestions }));
                              setShowSkillSuggestions(prev => ({ ...prev, [index]: suggestions.length > 0 }));
                            }}
                            onBlur={() => {
                              // Delay hiding suggestions to allow clicking
                              setTimeout(() => setShowSkillSuggestions(prev => ({ ...prev, [index]: false })), 150);
                            }}
                          />
                          <button
                            onClick={() => addSkill(index, skillInput[index] || '')}
                            className="bg-stone-600 hover:bg-stone-700 text-white px-4 py-2.5 rounded-lg transition-colors font-medium"
                          >
                            Add
                          </button>
                        </div>
                        {/* Suggestions dropdown */}
                        {showSkillSuggestions[index] && skillSuggestions[index] && skillSuggestions[index].length > 0 && (
                          <div className="absolute z-10 w-full bg-white border border-stone-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                            {skillSuggestions[index].map((suggestion, suggIndex) => (
                              <button
                                key={suggIndex}
                                className="w-full text-left px-3 py-2 hover:bg-stone-50 transition-colors border-b border-stone-100 last:border-b-0 text-sm"
                                onMouseDown={(e) => {
                                  e.preventDefault(); // Prevent input blur
                                  selectSkillSuggestion(index, suggestion);
                                }}
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Empty state or Add New Button */}
              {(!active?.capabilities?.skills || active.capabilities.skills.length === 0) ? (
                <div className="text-center py-12 bg-stone-50/50 rounded-xl border-2 border-dashed border-stone-200">
                  <div className="text-6xl mb-4">🎯</div>
                  <h3 className="text-lg font-medium text-stone-900 mb-2">No Skills Added Yet</h3>
                  <p className="text-stone-500 mb-4">Showcase your technical and professional expertise</p>
                  <button 
                    className="px-6 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-lg transition-colors font-semibold"
                    onClick={() => handleField('capabilities.skills', [{ type: 'Programming Languages', details: [] }])}
                  >
                    Add Your First Skill Group
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => handleField('capabilities.skills', [...(active?.capabilities?.skills || []), { type: '', details: [] }])}
                  className="w-full bg-stone-600 hover:bg-stone-700 text-white font-semibold py-4 px-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center space-x-3"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                  </svg>
                  <span>Add New Skill Group</span>
                </button>
              )}
            </div>
          </section>
        )}

        {tab === 'Certificates' && (
          <section className="mt-6">
            <div className="space-y-6">
              {(active?.capabilities?.certificates || []).map((cert, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm">
                  <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 100-2H7a1 1 0 100 2h6zm-6 4a1 1 0 100-2h6a1 1 0 100 2H7z" clipRule="evenodd"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-semibold text-stone-900">{cert.certificate || 'Professional Certification'}</h3>
                          <p className="text-sm text-stone-500">{cert.issuer || 'Certification Authority'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const newCerts = [...(active?.capabilities?.certificates || [])];
                          newCerts.splice(index, 1);
                          handleField('capabilities.certificates', newCerts);
                        }}
                        className="text-stone-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
                        title="Remove certificate"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative">
                          <label className="block text-sm font-medium text-stone-700 mb-2">Certificate Name</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., AWS Certified Solutions Architect, PMP..."
                            value={cert.certificate || ''}
                            onChange={e => {
                              const newCerts = [...(active?.capabilities?.certificates || [])];
                              newCerts[index].certificate = e.target.value;
                              handleField('capabilities.certificates', newCerts);
                              handleCertificateSuggestion(e.target.value, index, 'certificate');
                            }}
                            onFocus={() => {
                              handleCertificateSuggestion(cert.certificate || '', index, 'certificate');
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                const key = `${index}-certificate`;
                                setShowCertificateSuggestions(prev => ({ ...prev, [key]: false }));
                              }, 150);
                            }}
                          />
                          {/* Certificate suggestions dropdown */}
                          {showCertificateSuggestions[`${index}-certificate`] && certificateSuggestions[`${index}-certificate`] && certificateSuggestions[`${index}-certificate`].length > 0 && (
                            <div className="absolute z-10 w-full bg-white border border-stone-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto">
                              {certificateSuggestions[`${index}-certificate`].map((suggestion, suggIndex) => (
                                <button
                                  key={suggIndex}
                                  className="w-full text-left px-3 py-2 hover:bg-stone-50 transition-colors border-b border-stone-100 last:border-b-0 text-sm"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectCertificateSuggestion(index, 'certificate', suggestion);
                                  }}
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Issuing Organization</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., Amazon Web Services"
                            value={cert.issuer || ''}
                            onChange={e => {
                              const newCerts = [...(active?.capabilities?.certificates || [])];
                              newCerts[index].issuer = e.target.value;
                              handleField('capabilities.certificates', newCerts);
                            }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Date Obtained</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., March 2023"
                            value={cert.start || ''}
                            onChange={e => {
                              const newCerts = [...(active?.capabilities?.certificates || [])];
                              newCerts[index].start = e.target.value;
                              handleField('capabilities.certificates', newCerts);
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Expiry Date (Optional)</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., March 2026 or Never Expires"
                            value={cert.end || ''}
                            onChange={e => {
                              const newCerts = [...(active?.capabilities?.certificates || [])];
                              newCerts[index].end = e.target.value;
                              handleField('capabilities.certificates', newCerts);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {(!active?.capabilities?.certificates || active.capabilities.certificates.length === 0) ? (
                <div className="text-center py-12 bg-stone-50/50 rounded-xl border-2 border-dashed border-stone-200">
                  <div className="text-6xl mb-4">🏅</div>
                  <h3 className="text-lg font-medium text-stone-900 mb-2">No Certificates Added Yet</h3>
                  <p className="text-stone-500 mb-4">Showcase your professional certifications and qualifications</p>
                  <button 
                    className="px-6 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-lg transition-colors font-semibold"
                    onClick={() => handleField('capabilities.certificates', [{ certificate: '', issuer: '', start: '', end: '' }])}
                  >
                    Add Your First Certificate
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => handleField('capabilities.certificates', [...(active?.capabilities?.certificates || []), { certificate: '', issuer: '', start: '', end: '' }])}
                  className="w-full bg-stone-600 hover:bg-stone-700 text-white font-semibold py-4 px-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center space-x-3"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                  </svg>
                  <span>Add New Certificate</span>
                </button>
              )}
            </div>
          </section>
        )}

        {tab === 'Activities' && (
          <section className="mt-6">
            <div className="space-y-6">
              {(active?.background?.activities || []).map((activity, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl shadow-sm">
                  <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-semibold text-stone-900">{activity.activity || 'Activity'}</h3>
                          <p className="text-sm text-stone-500">{activity.position || 'Position/Role'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const newActivities = [...(active?.background?.activities || [])];
                          newActivities.splice(index, 1);
                          handleField('background.activities', newActivities);
                        }}
                        className="text-stone-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
                        title="Remove activity"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Basic Information */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Activity Name</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., Student Government, Volunteer Work"
                            value={activity.activity || ''}
                            onChange={e => {
                              const newActivities = [...(active?.background?.activities || [])];
                              newActivities[index].activity = e.target.value;
                              handleField('background.activities', newActivities);
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Position/Role</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., President, Volunteer"
                            value={activity.position || ''}
                            onChange={e => {
                              const newActivities = [...(active?.background?.activities || [])];
                              newActivities[index].position = e.target.value;
                              handleField('background.activities', newActivities);
                            }}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Start Date</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., Jan 2022"
                            value={activity.start || ''}
                            onChange={e => {
                              const newActivities = [...(active?.background?.activities || [])];
                              newActivities[index].start = e.target.value;
                              handleField('background.activities', newActivities);
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">End Date</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50"
                            placeholder="e.g., Present"
                            value={activity.end || ''}
                            onChange={e => {
                              const newActivities = [...(active?.background?.activities || [])];
                              newActivities[index].end = e.target.value;
                              handleField('background.activities', newActivities);
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Details - Single textarea for all bullet points */}
                    <div className="bg-stone-50/50 rounded-lg p-4 border border-stone-100">
                      <h4 className="font-medium text-stone-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        Accomplishments & Responsibilities
                      </h4>
                      <textarea 
                        className="w-full px-3 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white resize-none text-sm leading-relaxed" 
                        rows={5}
                        placeholder="• Organized fundraising events that raised $5,000 for local charity
• Led team of 15 volunteers in community outreach programs
• Coordinated with local businesses to secure sponsorship deals
• Improved event attendance by 30% through social media campaigns"
                        value={(activity.details || []).join('\n')}
                        onChange={e => {
                          const newActivities = [...(active?.background?.activities || [])];
                          newActivities[index].details = e.target.value.split('\n').map(line => line.trim()).filter(Boolean);
                          handleField('background.activities', newActivities);
                        }}
                      />
                      <p className="text-xs text-stone-500 mt-2">Enter your accomplishments and responsibilities, one per line. Use bullet points (•) for better formatting.</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {(!active?.background?.activities || active.background.activities.length === 0) ? (
                <div className="text-center py-12 bg-stone-50/50 rounded-xl border-2 border-dashed border-stone-200">
                  <div className="text-6xl mb-4">🌟</div>
                  <h3 className="text-lg font-medium text-stone-900 mb-2">No Activities Added Yet</h3>
                  <p className="text-stone-500 mb-4">Showcase your extracurricular activities, volunteer work, and leadership roles</p>
                  <button 
                    className="px-6 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-lg transition-colors font-semibold"
                    onClick={() => handleField('background.activities', [{ activity: '', position: '', start: '', end: '', details: [] }])}
                  >
                    Add Your First Activity
                  </button>
                </div>
              ) : (
                <button 
                  onClick={() => handleField('background.activities', [...(active?.background?.activities || []), { activity: '', position: '', start: '', end: '', details: [] }])}
                  className="w-full bg-stone-600 hover:bg-stone-700 text-white font-semibold py-4 px-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center space-x-3"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                  </svg>
                  <span>Add New Activity</span>
                </button>
              )}
            </div>
          </section>
        )}

        {tab === 'Preferences' && (
          <section className="space-y-6">
            {/* Career Directions Section */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="bg-gradient-to-r from-blue-50 to-primary-50 px-6 py-4 border-b border-blue-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 text-lg">🎯</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {active?.preferences?.selected_career_directions 
                        ? `Selected Career Directions (${active.preferences.selected_career_directions.length})`
                        : 'Career Directions'
                      }
                    </h3>
                    <p className="text-sm text-gray-600">
                      {active?.preferences?.selected_career_directions 
                        ? 'Career directions you\'ve selected and can build resumes for'
                        : 'Your AI-generated career paths and preferences'
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {(active?.preferences?.selected_career_directions || active?.preferences?.career_directions?.filter(d => d.is_primary) || []).length > 0 ? (
                  <div className="space-y-4">
                    {/* Show selected career directions if available, otherwise fall back to primary directions */}
                    {(active?.preferences?.selected_career_directions || 
                      (showAllDirections 
                        ? (active.preferences.career_directions || [])
                        : (active.preferences.career_directions || []).filter(direction => direction.is_primary)
                      )
                    ).map((direction, index) => (
                      <div key={direction.id || index} className={`border-2 rounded-lg p-4 transition-all ${
                        active?.preferences?.selected_career_directions 
                          ? 'border-green-500 bg-green-50' 
                          : direction.is_primary 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="text-lg font-semibold text-gray-900">{direction.title}</h4>
                              {active?.preferences?.selected_career_directions ? (
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                                  Selected
                                </span>
                              ) : direction.is_primary && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                  Primary
                                </span>
                              )}
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                {Math.round(direction.relevance_score)}% match
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{direction.reason}</p>
                          </div>
                          
                          {/* Remove Direction Button */}
                          {(direction.is_primary || active?.preferences?.selected_career_directions) && (
                            <button
                              onClick={async () => {
                                const isSelected = active?.preferences?.selected_career_directions;
                                const confirmMessage = isSelected 
                                  ? `Are you sure you want to remove "${direction.title}" from your selected career directions?`
                                  : `Are you sure you want to remove "${direction.title}" as a primary direction?`;
                                  
                                if (window.confirm(confirmMessage)) {
                                  try {
                                    setSaving(true);
                                    
                                    if (isSelected) {
                                      // Remove from selected_career_directions
                                      const updatedSelectedDirections = active.preferences.selected_career_directions.filter(
                                        d => d.id !== direction.id
                                      );
                                      
                                      await professionalProfileApi.updateProfile(active._id, {
                                        'preferences.selected_career_directions': updatedSelectedDirections
                                      });
                                    } else {
                                      // Update the direction to set is_primary to false
                                      const updatedDirections = active.preferences.career_directions.map(dir => 
                                        dir.id === direction.id ? { ...dir, is_primary: false } : dir
                                      );
                                      
                                      await professionalProfileApi.updateProfile(active._id, {
                                        'preferences.career_directions': updatedDirections
                                      });
                                    }
                                    
                                    toast.success('Direction removed successfully!');
                                    await loadProfiles();
                                  } catch (e) {
                                    console.error(e);
                                    toast.error('Failed to remove direction');
                                  } finally {
                                    setSaving(false);
                                  }
                                }
                              }}
                              className="ml-4 p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove this direction"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                              </svg>
                            </button>
                          )}
                        </div>
                        
                        <div className="mt-3">
                          <div className="flex-1">
                            
                            {/* Show Goals instead of Strengths/Weaknesses */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <h5 className="font-medium text-blue-700 mb-1">Short-term Goals</h5>
                                <ul className="text-blue-600 space-y-1">
                                  {(direction.short_term_goals || []).slice(0, 3).map((goal, i) => (
                                    <li key={i}>• {goal}</li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h5 className="font-medium text-primary-700 mb-1">Long-term Goals</h5>
                                <ul className="text-primary-600 space-y-1">
                                  {(direction.long_term_goals || []).slice(0, 3).map((goal, i) => (
                                    <li key={i}>• {goal}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                            
                            <div className="mt-3">
                              <h5 className="font-medium text-green-700 mb-1">Recommended Skills</h5>
                              <div className="flex flex-wrap gap-1">
                                {(direction.recommended_skills || []).slice(0, 5).map((skill, i) => (
                                  <span key={i} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Add New Direction Button - only show if not showing all directions */}
                    {!showAllDirections && (active.preferences.career_directions || []).filter(d => !d.is_primary).length > 0 && (
                      <div className="text-center py-4">
                        <button
                          onClick={() => setShowDirectionModal(true)}
                          className="px-6 py-3 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
                        >
                          + Add New Career Direction ({(active.preferences.career_directions || []).filter(d => !d.is_primary).length} available)
                        </button>
                      </div>
                    )}
                    
                    {/* Show fewer directions button if currently showing all */}
                    {showAllDirections && (active.preferences.career_directions || []).filter(d => d.is_primary).length > 0 && (
                      <div className="text-center py-4">
                        <button
                          onClick={() => setShowAllDirections(false)}
                          className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                        >
                          Show Only Primary Directions
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-gray-400 text-2xl">🎯</span>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No Career Directions Yet</h4>
                    <p className="text-gray-600 mb-4">Complete the profile building process to get AI-generated career directions.</p>
                    <button 
                      onClick={() => setShowGuide(true)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all duration-200"
                    >
                      Start Profile Building
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              {/* Header */}
              <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Career Preferences</h3>
                    <p className="text-sm text-stone-500">Your job search criteria and work preferences</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Legal Status */}
                <div className="space-y-4">
                  <h4 className="font-medium text-stone-700">Legal & Location</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Visa Status</label>
                      <div className="flex space-x-2">
                        <select className="flex-1 px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" value={active?.visa_status || ''} onChange={e => handleField('visa_status', e.target.value)}>
                          <option value="">Select status...</option>
                          {visaOptions.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                        {active?.visa_status === 'Other' && (
                          <input className="flex-1 px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" placeholder="Specify" onChange={e => handleField('visa_status', e.target.value)} />
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Remote Preference</label>
                      <select className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" value={active?.career_preference?.remote_preference || ''} onChange={e => handleField('career_preference', { ...(active?.career_preference || {}), remote_preference: e.target.value })}>
                        <option value="">Select preference...</option>
                        {remoteOptions.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Job Type & Company */}
                <div className="space-y-4">
                  <h4 className="font-medium text-stone-700">Employment Preferences</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Employment Type</label>
                      <select multiple size="3" className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" value={active?.career_preference?.employment_type || []} onChange={e => {
                        const vals = Array.from(e.target.selectedOptions).map(o => o.value);
                        handleField('career_preference', { ...(active?.career_preference || {}), employment_type: vals });
                      }}>
                        {employmentTypeOptions.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <p className="text-xs text-stone-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Company Size</label>
                      <select multiple size="3" className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" value={active?.career_preference?.company_size || []} onChange={e => {
                        const vals = Array.from(e.target.selectedOptions).map(o => o.value);
                        handleField('career_preference', { ...(active?.career_preference || {}), company_size: vals });
                      }}>
                        {companySizeOptions.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <p className="text-xs text-stone-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Career Level</label>
                    <select className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50 md:w-1/2" value={active?.career_preference?.career_level || ''} onChange={e => handleField('career_preference', { ...(active?.career_preference || {}), career_level: e.target.value })}>
                      <option value="">Select level...</option>
                      {careerLevelOptions.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>

                {/* Interests */}
                <div className="space-y-4">
                  <h4 className="font-medium text-stone-700">Career Interests</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Desired Roles</label>
                      <input 
                        className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" 
                        placeholder="e.g., Software Engineer, Product Manager, Data Analyst"
                        value={(active?.career_preference?.desired_roles||[]).join(', ')} 
                        onChange={e=>handleField('career_preference',{...(active?.career_preference||{}), desired_roles: e.target.value.split(',').map(x=>x.trim()).filter(Boolean)})} 
                      />
                      <p className="text-xs text-stone-500 mt-1">Separate multiple roles with commas</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Target Industries</label>
                      <input 
                        className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50" 
                        placeholder="e.g., Technology, Healthcare, Finance, Education"
                        value={(active?.career_preference?.industries||[]).join(', ')} 
                        onChange={e=>handleField('career_preference',{...(active?.career_preference||{}), industries: e.target.value.split(',').map(x=>x.trim()).filter(Boolean)})} 
                      />
                      <p className="text-xs text-stone-500 mt-1">Separate multiple industries with commas</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === 'Engagement' && (
          <section className="mt-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              {/* Header */}
              <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Personality Profile</h3>
                    <p className="text-sm text-stone-500">Your MBTI type and work style preferences</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* MBTI Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">MBTI Personality Type</label>
                    <select 
                      className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50 md:w-1/2" 
                      value={active?.personality?.mbti || ''} 
                      onChange={e => {
                        const v = e.target.value;
                        const preset = MBTI_PRESETS[v] || {};
                        handleField('personality', { 
                          ...(active?.personality || {}), 
                          mbti: v, 
                          traits: preset.traits || [], 
                          work_style: preset.work_style || '', 
                          leadership_style: preset.leadership_style || '' 
                        });
                      }}
                    >
                      <option value="">Select your MBTI type...</option>
                      {MBTI_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <p className="text-xs text-stone-500 mt-1">
                      Don't know your type?{' '}
                      <a 
                        href="https://www.16personalities.com/personality-types" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-stone-600 hover:text-stone-800 underline font-medium"
                      >
                        Take a free assessment online
                      </a>
                    </p>
                  </div>
                </div>

                {/* Editable traits and work style */}
                {active?.personality?.mbti && (
                  <div className="bg-stone-50/50 rounded-lg p-4 border border-stone-100 space-y-4">
                    <h4 className="font-medium text-stone-900">Your Profile Based on {active.personality.mbti}</h4>
                    <p className="text-xs text-stone-500 mb-4">These are auto-filled based on your MBTI type, but you can edit them to better match your personal experience.</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Key Traits</label>
                        <input
                          className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white text-sm"
                          placeholder="e.g., Analytical, Creative, Detail-oriented..."
                          value={(active?.personality?.traits||[]).join(', ')}
                          onChange={e => {
                            const traits = e.target.value.split(',').map(trait => trait.trim()).filter(Boolean);
                            handleField('personality', { 
                              ...(active?.personality || {}), 
                              traits: traits
                            });
                          }}
                        />
                        <p className="text-xs text-stone-500 mt-1">Separate multiple traits with commas</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">Work Style</label>
                        <input
                          className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white text-sm"
                          placeholder="e.g., Collaborative team player with attention to detail..."
                          value={active?.personality?.work_style || ''}
                          onChange={e => {
                            handleField('personality', { 
                              ...(active?.personality || {}), 
                              work_style: e.target.value
                            });
                          }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Leadership Style</label>
                      <input
                        className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white text-sm"
                        placeholder="e.g., Transformational leader who empowers team members..."
                        value={active?.personality?.leadership_style || ''}
                        onChange={e => {
                          handleField('personality', { 
                            ...(active?.personality || {}), 
                            leadership_style: e.target.value
                          });
                        }}
                      />
                      <p className="text-xs text-stone-500 mt-1">Describe your leadership approach (optional)</p>
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {!active?.personality?.mbti && (
                  <div className="text-center py-8 text-stone-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-stone-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
                    </svg>
                    <p>Select your MBTI type above to see your personality profile</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {tab === 'Goals' && (
          <section className="mt-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              {/* Header */}
              <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Professional Goals</h3>
                    <p className="text-sm text-stone-500">Your career aspirations and learning objectives</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Short Term Goals */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-stone-600 rounded-full"></div>
                      <label className="text-sm font-medium text-stone-700">Short Term Goals (1-2 years)</label>
                    </div>
                    <textarea 
                      className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50 resize-none" 
                      rows={5} 
                      placeholder="Enter your short-term goals, one per line..."
                      value={(active?.professional_goals?.short_term||[]).join('\n')} 
                      onChange={e=>handleField('professional_goals',{...(active?.professional_goals||{}), short_term: e.target.value.split('\n').map(x=>x.trim()).filter(Boolean)})} 
                    />
                    <p className="text-xs text-stone-500">One goal per line</p>
                  </div>

                  {/* Long Term Goals */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-stone-600 rounded-full"></div>
                      <label className="text-sm font-medium text-stone-700">Long Term Goals (3-5 years)</label>
                    </div>
                    <textarea 
                      className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50 resize-none" 
                      rows={5} 
                      placeholder="Enter your long-term career aspirations..."
                      value={(active?.professional_goals?.long_term||[]).join('\n')} 
                      onChange={e=>handleField('professional_goals',{...(active?.professional_goals||{}), long_term: e.target.value.split('\n').map(x=>x.trim()).filter(Boolean)})} 
                    />
                    <p className="text-xs text-stone-500">One goal per line</p>
                  </div>

                  {/* Learning Interests */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-stone-600 rounded-full"></div>
                      <label className="text-sm font-medium text-stone-700">Learning & Development</label>
                    </div>
                    <textarea 
                      className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-stone-50/50 resize-none" 
                      rows={5} 
                      placeholder="Skills you want to learn or improve..."
                      value={(active?.professional_goals?.learning_interests||[]).join('\n')} 
                      onChange={e=>handleField('professional_goals',{...(active?.professional_goals||{}), learning_interests: e.target.value.split('\n').map(x=>x.trim()).filter(Boolean)})} 
                    />
                    <p className="text-xs text-stone-500">Skills, technologies, or areas of interest</p>
                  </div>
                </div>

                {/* Empty state encouragement */}
                {(!active?.professional_goals?.short_term?.length && !active?.professional_goals?.long_term?.length && !active?.professional_goals?.learning_interests?.length) && (
                  <div className="text-center py-8 bg-stone-50/50 rounded-lg border border-stone-100 mt-6">
                    <svg className="w-12 h-12 mx-auto mb-3 text-stone-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd"/>
                    </svg>
                    <h4 className="text-stone-900 font-medium mb-2">Set Your Professional Goals</h4>
                    <p className="text-stone-500 text-sm">Define your career aspirations to guide your professional journey</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {tab === 'References' && (
          <section className="mt-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              {/* Header */}
              <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Professional References</h3>
                    <p className="text-sm text-stone-500">People who can vouch for your professional experience</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-6">
                  {(active?.references || []).map((reference, index) => (
                    <div key={index} className="bg-stone-50/50 rounded-lg p-4 border border-stone-100">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-stone-900">Reference #{index + 1}</h4>
                        <button
                          onClick={() => {
                            const newReferences = [...(active?.references || [])];
                            newReferences.splice(index, 1);
                            handleField('references', newReferences);
                          }}
                          className="text-stone-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
                          title="Remove this reference"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                          </svg>
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Full Name</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white"
                            placeholder="e.g., John Smith"
                            value={reference.name || ''}
                            onChange={e => {
                              const newReferences = [...(active?.references || [])];
                              newReferences[index].name = e.target.value;
                              handleField('references', newReferences);
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Relationship</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white"
                            placeholder="e.g., Former Manager, Colleague"
                            value={reference.relationship || ''}
                            onChange={e => {
                              const newReferences = [...(active?.references || [])];
                              newReferences[index].relationship = e.target.value;
                              handleField('references', newReferences);
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Company</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white"
                            placeholder="e.g., Tech Corp"
                            value={reference.company || ''}
                            onChange={e => {
                              const newReferences = [...(active?.references || [])];
                              newReferences[index].company = e.target.value;
                              handleField('references', newReferences);
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Position</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white"
                            placeholder="e.g., Senior Director"
                            value={reference.position || ''}
                            onChange={e => {
                              const newReferences = [...(active?.references || [])];
                              newReferences[index].position = e.target.value;
                              handleField('references', newReferences);
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Email</label>
                          <input
                            type="email"
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white"
                            placeholder="e.g., john.smith@techcorp.com"
                            value={reference.email || ''}
                            onChange={e => {
                              const newReferences = [...(active?.references || [])];
                              newReferences[index].email = e.target.value;
                              handleField('references', newReferences);
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Phone</label>
                          <input
                            type="tel"
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white"
                            placeholder="e.g., 555-123-4567"
                            value={reference.phone || ''}
                            onChange={e => {
                              const newReferences = [...(active?.references || [])];
                              newReferences[index].phone = e.target.value;
                              handleField('references', newReferences);
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            className="rounded border-stone-300 text-stone-600 focus:ring-stone-500 focus:ring-offset-0"
                            checked={reference.permission_granted || false}
                            onChange={e => {
                              const newReferences = [...(active?.references || [])];
                              newReferences[index].permission_granted = e.target.checked;
                              handleField('references', newReferences);
                            }}
                          />
                          <span className="text-sm text-stone-700">I have permission to use this person as a reference</span>
                        </label>
                      </div>
                    </div>
                  ))}
                  
                  {/* Add new reference button */}
                  <button
                    onClick={() => {
                      const newReference = { 
                        name: '', 
                        relationship: '', 
                        company: '', 
                        position: '', 
                        email: '', 
                        phone: '', 
                        permission_granted: false 
                      };
                      const newReferences = [...(active?.references || []), newReference];
                      handleField('references', newReferences);
                    }}
                    className="w-full bg-stone-600 hover:bg-stone-700 text-white font-semibold py-4 px-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center space-x-3"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                    </svg>
                    <span>Add Reference</span>
                  </button>
                </div>

                {/* Empty state */}
                {(!active?.references || active.references.length === 0) && (
                  <div className="text-center py-12 bg-stone-50/50 rounded-xl border-2 border-dashed border-stone-200">
                    <div className="text-6xl mb-4">👥</div>
                    <h4 className="text-stone-900 font-medium mb-2">No References Added</h4>
                    <p className="text-stone-500 text-sm mb-4">Add professional references who can speak to your work experience</p>
                    <button
                      onClick={() => {
                        const newReference = { 
                          name: '', 
                          relationship: '', 
                          company: '', 
                          position: '', 
                          email: '', 
                          phone: '', 
                          permission_granted: false 
                        };
                        handleField('references', [newReference]);
                      }}
                      className="px-6 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-lg transition-colors font-semibold"
                    >
                      Add Your First Reference
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {tab === 'Additional' && (
          <section className="mt-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              {/* Header */}
              <div className="bg-gradient-to-r from-stone-50 to-slate-50 px-6 py-4 border-b border-stone-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-stone-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Additional Details</h3>
                    <p className="text-sm text-stone-500">Extra information that doesn't fit in other sections</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-6">
                  {(Array.isArray(active?.additional_details) ? active.additional_details : []).map((detail, index) => (
                    <div key={index} className="bg-stone-50/50 rounded-lg p-4 border border-stone-100">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-stone-900">Additional Detail #{index + 1}</h4>
                        <button
                          onClick={() => {
                            const newDetails = [...(Array.isArray(active?.additional_details) ? active.additional_details : [])];
                            newDetails.splice(index, 1);
                            handleField('additional_details', newDetails);
                          }}
                          className="text-stone-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
                          title="Remove this detail"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                          </svg>
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Question</label>
                          <input
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white"
                            placeholder="e.g., What languages do you speak?"
                            value={detail.question || ''}
                            onChange={e => {
                              const newDetails = [...(Array.isArray(active?.additional_details) ? active.additional_details : [])];
                              newDetails[index].question = e.target.value;
                              handleField('additional_details', newDetails);
                            }}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">Answer</label>
                          <textarea
                            className="w-full px-3 py-2.5 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-200 focus:border-stone-400 transition-colors bg-white resize-none"
                            rows={3}
                            placeholder="Try to provide more details."
                            value={detail.answer || ''}
                            onChange={e => {
                              const newDetails = [...(Array.isArray(active?.additional_details) ? active.additional_details : [])];
                              newDetails[index].answer = e.target.value;
                              handleField('additional_details', newDetails);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Add new detail button */}
                  <button
                    onClick={() => {
                      const newDetails = [...(Array.isArray(active?.additional_details) ? active.additional_details : []), { question: '', answer: '' }];
                      handleField('additional_details', newDetails);
                    }}
                    className="w-full bg-stone-600 hover:bg-stone-700 text-white font-semibold py-4 px-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center space-x-3"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
                    </svg>
                    <span>Add Additional Detail</span>
                  </button>
                </div>

                {/* Empty state */}
                {(!Array.isArray(active?.additional_details) || active.additional_details.length === 0) && (
                  <div className="text-center py-12 bg-stone-50/50 rounded-xl border-2 border-dashed border-stone-200">
                    <div className="text-6xl mb-4">💡</div>
                    <h4 className="text-stone-900 font-medium mb-2">No Additional Details</h4>
                    <p className="text-stone-500 text-sm mb-4">Add extra information like languages, clearances, or availability</p>
                    <button
                      onClick={() => {
                        const newDetails = [{ question: '', answer: '' }];
                        handleField('additional_details', newDetails);
                      }}
                      className="px-6 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-lg transition-colors font-semibold"
                    >
                      Add Your First Detail
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
        
        </div>
      </div>

      {/* Direction Selection Modal */}
      {showDirectionModal && createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-xl font-bold text-gray-900">Select Additional Career Direction</h3>
              <button
                onClick={() => {
                  setShowDirectionModal(false);
                  setSelectedNewDirection(null);
                  setFollowupAnswers({});
                  setFollowupContextSelections({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              {!selectedNewDirection ? (
                <div>
                  <p className="text-gray-600 mb-6">Choose an additional career path to explore:</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(active?.preferences?.career_directions || [])
                      .filter(direction => !direction.is_primary)
                      .map((direction, i) => (
                        <div 
                          key={i} 
                          className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
                          onClick={() => {
                            setSelectedNewDirection(direction);
                            setFollowupAnswers({});
                            setFollowupContextSelections({});
                          }}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <h4 className="text-lg font-semibold text-gray-800">{direction.title}</h4>
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              {Math.round(direction.relevance_score)}% match
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">{direction.reason}</p>
                          
                          {direction.goals?.short_term?.length > 0 && (
                            <div className="mb-4">
                              <h5 className="text-sm font-medium text-gray-700 mb-2">Goals:</h5>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {direction.goals.short_term.slice(0, 3).map((goal, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="text-blue-500 mr-2">•</span>
                                    {goal}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center text-blue-600 text-sm font-medium">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Select this direction
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                  
                  {(active?.preferences?.career_directions || []).filter(d => !d.is_primary).length === 0 && (
                    <div className="text-center text-gray-500 py-8">
                      <p>All available directions have been selected as primary.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Answer Questions about {selectedNewDirection.title}</h4>
                    <p className="text-sm text-gray-600">Help us understand your experience better by answering these follow-up questions.</p>
                  </div>
                  
                  {/* Strengths and Weaknesses Section */}
                  {(selectedNewDirection.strengths?.length > 0 || selectedNewDirection.weaknesses?.length > 0) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">💪 Your Profile Analysis</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {selectedNewDirection.strengths?.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-green-700 mb-3 flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                              </svg>
                              Strengths for this role
                            </h5>
                            <ul className="space-y-2">
                              {selectedNewDirection.strengths.map((strength, idx) => (
                                <li key={idx} className="text-sm text-gray-700 flex items-start">
                                  <span className="text-green-500 mr-2 mt-0.5">•</span>
                                  {strength}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {selectedNewDirection.weaknesses?.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-orange-700 mb-3 flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                              </svg>
                              Areas to develop
                            </h5>
                            <ul className="space-y-2">
                              {selectedNewDirection.weaknesses.map((weakness, idx) => (
                                <li key={idx} className="text-sm text-gray-700 flex items-start">
                                  <span className="text-orange-500 mr-2 mt-0.5">•</span>
                                  {weakness}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-6">
                    {(selectedNewDirection.followup_questions || []).map((question, i) => {
                      const questionText = question.question || question;
                      const questionKey = typeof question === 'string' ? question : question.question;
                      const questionContext = question.context || 'general';
                      
                      // Use the context field from the question to determine what selections to show
                      const isExperienceQuestion = questionContext === 'experience';
                      const isSkillsQuestion = questionContext === 'skills';
                      const isEducationQuestion = questionContext === 'education';
                      const isCourseQuestion = questionContext === 'course';
                      
                      // Helper function to build context text
                      const buildContextText = (contextSelections) => {
                        let contextText = '';
                        if (contextSelections.companies?.length > 0) {
                          contextText += contextSelections.companies.map(company => `At ${company}`).join(', ') + ': ';
                        }
                        if (contextSelections.education?.length > 0) {
                          contextText += contextSelections.education.map(school => `At ${school}`).join(', ') + ': ';
                        }
                        if (contextSelections.courses?.length > 0) {
                          contextText += contextSelections.courses.map(school => `At ${school}`).join(', ') + ': ';
                        }
                        if (contextSelections.recommendedSkills?.length > 0) {
                          contextText += `With ${contextSelections.recommendedSkills.join(', ')}: `;
                        }
                        return contextText;
                      };
                      
                      const contextSelections = followupContextSelections[questionKey] || {};
                      const contextText = buildContextText(contextSelections);
                      const baseAnswer = followupAnswers[questionKey] || '';
                      const displayValue = contextText + baseAnswer;

                      return (
                        <div key={i} className="bg-gray-50 rounded-lg p-4">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            {questionText}
                          </label>
                          
                          {isExperienceQuestion && active?.background?.experience?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-500 mb-2">Select relevant companies:</p>
                              <div className="flex flex-wrap gap-2">
                                {active.background.experience.map((exp, expIndex) => (
                                  <label key={expIndex} className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300"
                                      checked={followupContextSelections[questionKey]?.companies?.includes(exp.company) || false}
                                      onChange={e => {
                                        const companies = followupContextSelections[questionKey]?.companies || [];
                                        const updatedCompanies = e.target.checked 
                                          ? [...companies, exp.company]
                                          : companies.filter(c => c !== exp.company);
                                        setFollowupContextSelections(prev => ({
                                          ...prev,
                                          [questionKey]: {
                                            ...prev[questionKey],
                                            companies: updatedCompanies
                                          }
                                        }));
                                      }}
                                    />
                                    <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                      {exp.company}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {isSkillsQuestion && selectedNewDirection?.recommended_skills && Array.isArray(selectedNewDirection.recommended_skills) && selectedNewDirection.recommended_skills.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-500 mb-2">Select relevant recommended skills:</p>
                              <div className="flex flex-wrap gap-2">
                                {selectedNewDirection.recommended_skills.map((skill, skillIndex) => (
                                  <label key={skillIndex} className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      className="form-checkbox h-4 w-4 text-green-600 rounded border-gray-300"
                                      checked={followupContextSelections[questionKey]?.recommendedSkills?.includes(skill) || false}
                                      onChange={e => {
                                        const recommendedSkills = followupContextSelections[questionKey]?.recommendedSkills || [];
                                        const updatedRecommendedSkills = e.target.checked 
                                          ? [...recommendedSkills, skill]
                                          : recommendedSkills.filter(s => s !== skill);
                                        setFollowupContextSelections(prev => ({
                                          ...prev,
                                          [questionKey]: {
                                            ...prev[questionKey],
                                            recommendedSkills: updatedRecommendedSkills
                                          }
                                        }));
                                      }}
                                    />
                                    <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                      {skill}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                              

                          {isEducationQuestion && active?.background?.education?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-500 mb-2">Select relevant education:</p>
                              <div className="flex flex-wrap gap-2">
                                {active.background.education.map((edu, eduIndex) => (
                                  <label key={eduIndex} className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      className="form-checkbox h-4 w-4 text-primary-600 rounded border-gray-300"
                                      checked={followupContextSelections[questionKey]?.education?.includes(edu.school) || false}
                                      onChange={e => {
                                        const education = followupContextSelections[questionKey]?.education || [];
                                        const updatedEducation = e.target.checked 
                                          ? [...education, edu.school]
                                          : education.filter(s => s !== edu.school);
                                        setFollowupContextSelections(prev => ({
                                          ...prev,
                                          [questionKey]: {
                                            ...prev[questionKey],
                                            education: updatedEducation
                                          }
                                        }));
                                      }}
                                    />
                                    <span className="ml-2 text-sm bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                      {edu.school} {edu.degree && `- ${edu.degree}`}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {isCourseQuestion && active?.background?.education?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-500 mb-2">Select relevant university/course:</p>
                              <div className="flex flex-wrap gap-2">
                                {active.background.education.map((edu, eduIndex) => (
                                  <label key={eduIndex} className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      className="form-checkbox h-4 w-4 text-primary-600 rounded border-gray-300"
                                      checked={followupContextSelections[questionKey]?.courses?.includes(edu.school) || false}
                                      onChange={e => {
                                        const courses = followupContextSelections[questionKey]?.courses || [];
                                        const updatedCourses = e.target.checked 
                                          ? [...courses, edu.school]
                                          : courses.filter(s => s !== edu.school);
                                        setFollowupContextSelections(prev => ({
                                          ...prev,
                                          [questionKey]: {
                                            ...prev[questionKey],
                                            courses: updatedCourses
                                          }
                                        }));
                                      }}
                                    />
                                    <span className="ml-2 text-sm bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                      {edu.school} {edu.degree && `- ${edu.degree}`}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          <textarea 
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
                            rows={3}
                            placeholder="Share your experience or thoughts..."
                            value={displayValue}
                            onChange={e => {
                              // Remove context text from input value to get clean answer
                              let cleanAnswer = e.target.value;
                              if (contextText && cleanAnswer.startsWith(contextText)) {
                                cleanAnswer = cleanAnswer.substring(contextText.length);
                              }
                              
                              setFollowupAnswers(prev => ({
                                ...prev,
                                [questionKey]: cleanAnswer
                              }));
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex justify-between pt-6 border-t border-gray-200 mt-8">
                    <button 
                      className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                      onClick={() => setSelectedNewDirection(null)}
                    >
                      ← Back to Directions
                    </button>
                    <button 
                      className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                      onClick={async () => {
                        try {
                          setSaving(true);
                          
                          await professionalProfileApi.setPrimaryDirection(active._id, selectedNewDirection.id);
                          
                          const responses = (selectedNewDirection.followup_questions || []).map(q => {
                            const questionKey = typeof q === 'string' ? q : q.question;
                            const contextSelections = followupContextSelections[questionKey] || {};
                            
                            // Build context text for final answer
                            let contextText = '';
                            if (contextSelections.companies?.length > 0) {
                              contextText += contextSelections.companies.map(company => `At ${company}`).join(', ') + ': ';
                            }
                            if (contextSelections.education?.length > 0) {
                              contextText += contextSelections.education.map(school => `At ${school}`).join(', ') + ': ';
                            }
                            if (contextSelections.skills?.length > 0) {
                              contextText += `Using ${contextSelections.skills.join(', ')}: `;
                            }
                            if (contextSelections.recommendedSkills?.length > 0) {
                              contextText += `With ${contextSelections.recommendedSkills.join(', ')}: `;
                            }
                            
                            const finalAnswer = contextText + (followupAnswers[questionKey] || '');
                            
                            return {
                              answer: finalAnswer,
                              context: q.context || '',
                              contextSelections: contextSelections
                            };
                          });
                          
                          await professionalProfileApi.submitFollowupResponses(
                            active._id, 
                            selectedNewDirection.id, 
                            responses
                          );
                          
                          toast.success('Additional career direction added successfully!');
                          await loadProfiles();
                          
                          setShowDirectionModal(false);
                          setSelectedNewDirection(null);
                          setFollowupAnswers({});
                          setFollowupContextSelections({});
                          
                        } catch (e) {
                          console.error(e);
                          toast.error('Failed to add career direction');
                        } finally {
                          setSaving(false);
                        }
                      }}
                    >
                      {saving ? 'Adding Direction...' : 'Add This Direction'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <ProfessionalProfileCollection
        open={showGuide}
        onClose={() => setShowGuide(false)}
        profile={active}
        onProfileUpdate={(updatedProfile) => {
          setActive(updatedProfile);
        }}
        reloadProfiles={loadProfiles}
      />

      {/* Prompt Modal for New Users */}
      {showPromptModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-8">
            <div className="text-center">
              {/* Icon */}
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Build Your Professional Profile
              </h3>
              
              {/* Description */}
              <p className="text-gray-600 mb-8 leading-relaxed">
                It will help us if you give us more information about your career preferences, 
                experience, and goals. This will enable us to provide better recommendations 
                and personalized guidance.
              </p>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => {
                    setShowPromptModal(false);
                    setShowGuide(true);
                  }}
                  className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span>Guide Me</span>
                </button>
                
                <button
                  onClick={() => setShowPromptModal(false)}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Professional Profile</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-3">
                  Are you sure you want to delete your professional profile? This will permanently remove:
                </p>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• All your personal and contact information</li>
                  <li>• Your work experience and education history</li>
                  <li>• Your skills and capabilities</li>
                  <li>• Your career preferences and directions</li>
                  <li>• All AI-generated analysis and recommendations</li>
                </ul>
                <p className="text-red-600 font-medium mt-3">
                  This action cannot be undone!
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={deleteProfile}
                  disabled={deleting}
                  className={`flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${deleting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd"/>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                      </svg>
                      <span>Yes, Delete Profile</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}


